import React, { SyntheticEvent, useState } from 'react';

import {
	drag,
	lockOutlineClosed,
	lockOutlineOpen,
	more,
	plus,
} from '@/assets/icons';
import { AsaphDropdownMenu } from '@/components/shared/AsaphDropdownMenu';
import { RoundedButton } from '@/components/shared/RoundedButton/RoundedButton';
import { SmallSongCardButton } from '@/components/shared/SmallSongCardButton';
import {
	SmallTeamSongCard,
	SmallTeamSongCardProps,
} from '@/components/SmallTeamSongCard/SmallTeamSongCard';
import { classNames } from '@/helpers/classNames';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useSetlists } from '@/providers/SetlistsProvider';
import { useTeamSongs } from '@/providers/TeamSongsProvider';
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { isPlatform } from '@ionic/core';
import { SvgIcon } from '@material-ui/core';

import { SongOverride } from '../../GenerateSetlist';
import styles from './SortableItem.module.scss';

const isCapacitor = Capacitor.isNativePlatform();
const isIOS = isPlatform('ios') && isCapacitor;

export const SortableItem: React.FC<{
	id: string;
	songOverride?: SongOverride;
	isPostSermon?: boolean;
	isEdit?: boolean;
	isLightEdit?: boolean;
	reference?: string;
	isLocked?: boolean;
	isLoading?: boolean;
	onClick: SmallTeamSongCardProps['onClick'];
	onSwitchClick?: () => void;
	onLockClick?: () => void;
	onWarningClick?: (warning: React.ReactNode) => void;
	onViewAnalysisClick: () => void;
	onAddPassageClick: () => void;
	onDefaultKeyClick?: () => void;
	score?: number | null;
	displayKey?: boolean;
	isReorderMode?: boolean;
	onReorderClick?: () => void;
}> = ({
	id,
	songOverride,
	isPostSermon,
	isEdit,
	isLightEdit,
	reference,
	isLocked = false,
	isLoading = false,
	onClick,
	onSwitchClick,
	onLockClick,
	onViewAnalysisClick,
	onAddPassageClick,
	onDefaultKeyClick,
	score,
	displayKey = true,
	isReorderMode = false,
	onReorderClick,
}) => {
	const {
		attributes,
		listeners,
		setNodeRef,
		setActivatorNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id });
	const { previousSetlists } = useSetlists();
	const { findTeamSongsById } = useTeamSongs();
	const teamSongs = findTeamSongsById([id]);

	const [songMenuAnchorEl, setMenuAnchorEl] = useState<Element | null>(null);

	const fireOpenSongMenu = useAnalyticsEvent(
		'Generate_Setlist_Open_Song_Menu',
	);

	const handleDropdownOpen = (e: SyntheticEvent) => {
		fireOpenSongMenu({ teamSongId: id });
		setMenuAnchorEl(e.currentTarget);
	};
	const handleDropdownClose = () => {
		setMenuAnchorEl(null);
	};

	const open = !!songMenuAnchorEl;
	const teamSong = teamSongs[0];
	const isPlaceholder = !teamSong;
	const hasAnalysis = !!reference;

	const style = {
		transform: CSS.Translate.toString(transform),
		transition,
	};

	return (
		<div
			className={classNames([
				styles['song-card-draggable'],
				isDragging && styles['dragging'],
				isPostSermon && styles['post-sermon'],
				!!reference && styles['has-reference'],
				isPostSermon &&
					isPlaceholder &&
					styles['post-sermon-placeholder'],
			])}
			ref={setNodeRef}
			style={style}
			{...attributes}
		>
			{isPostSermon ? (
				<div className={styles['post-sermon-row']}>
					<span className={styles['post-sermon-label']}>
						{reference ? `For ${reference}` : 'Post Sermon Song'}
					</span>

					{!isPlaceholder && hasAnalysis && !isReorderMode ? (
						<RoundedButton
							color="white"
							onClick={onViewAnalysisClick}
						>
							View Analysis
						</RoundedButton>
					) : (
						!isPlaceholder &&
						(isEdit || isLightEdit) &&
						!isReorderMode && (
							<RoundedButton
								color="gradient"
								onClick={onAddPassageClick}
							>
								Add Passage
							</RoundedButton>
						)
					)}
				</div>
			) : null}
			<div
				className={classNames([
					styles['song-card-wrapper'],
					isLocked && styles['locked'],
				])}
			>
				{isPlaceholder ? (
					<SmallSongCardButton
						fullWidth
						icon={plus}
						onClick={onSwitchClick}
						className={styles['add-song-placeholder']}
					>
						Add Song
					</SmallSongCardButton>
				) : (
					<SmallTeamSongCard
						className={styles['song-card']}
						teamSongId={id}
						onClick={onClick}
						displayKey={displayKey}
						onKeyClick={isEdit ? onDefaultKeyClick : undefined}
						score={!isEdit ? score : undefined}
						override={songOverride}
						isLoading={isLoading}
					/>
				)}
				{isEdit && !isPlaceholder ? (
					isReorderMode ? (
						<div
							className={classNames([
								styles['handle'],
								styles['handle-reorder'],
							])}
						>
							<SvgIcon
								component={drag}
								ref={(e) =>
									setActivatorNodeRef(e as HTMLElement | null)
								}
								{...listeners}
							/>
						</div>
					) : (
						<>
							<SortableItemAction
								component={
									isLocked ? lockOutlineClosed : lockOutlineOpen
								}
								viewBox="0 0 15 18"
								className={classNames([
									styles['no-fill'],
									styles['lock'],
									styles[`lock-${isLocked ? 'closed' : 'open'}`],
									styles['first'],
								])}
								onClick={() => {
									onLockClick?.();
									Haptics.impact({
										style: ImpactStyle.Medium,
									});
								}}
							/>
							{/* <SortableItemAction component={lockOpened} onClick={onLockClick} /> */}
							<SortableItemAction
								component={more}
								onClick={handleDropdownOpen}
							/>
							<AsaphDropdownMenu
								className={styles['dropdown-menu']}
								open={open}
								anchorEl={songMenuAnchorEl}
								onClose={handleDropdownClose}
								items={[
									{
										label: 'Reorder',
										onClick: () => {
											onReorderClick?.();
											handleDropdownClose();
										},
									},
									{
										label: 'Swap song',
										onClick: () => {
											onSwitchClick?.();
											handleDropdownClose();
										},
									},
									{
										label: 'Change key',
										onClick: () => {
											onDefaultKeyClick?.();
											handleDropdownClose();
										},
									},
								]}
								anchorOrigin={{
									horizontal: 'right',
									vertical: 'top',
								}}
								transformOrigin={{
									horizontal: 'right',
									vertical: 'top',
								}}
							/>
						</>
					)
				) : null}
			</div>
		</div>
	);
};

const SortableItemAction: React.FC<
	React.HTMLAttributes<HTMLDivElement> & {
		component: React.ElementType;
		viewBox?: string;
	}
> = ({ component, className, viewBox, ...genericHtmlDivProps }) => {
	return (
		<div
			className={classNames([
				styles['action'],
				!isCapacitor && styles['hover'],
				className,
			])}
			{...genericHtmlDivProps}
		>
			<SvgIcon component={component} viewBox={viewBox} />
			<div className={styles['background']} />
		</div>
	);
};
